import { useContext, useMemo } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { addMinutes, format, isBefore, isDate } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock } from '@phosphor-icons/react';
import { CalendarDayContext } from '@/contexts/CalendarDayContext';
import { useRoutines } from '@/contexts/RoutineContext';
import { getRoutineStepLabel } from '@/utils/routine';
import { useThemePreference } from '@/contexts/ThemePreferenceContext';
import { getThemeTokens } from '@/utils/appThemes';

/**
 * Aktiviteter vars tid har passerat utan att de startats, i den vuxna brukarens dagsvy.
 *
 * TONFALLET ÄR HELA POÄNGEN med den här versionen. Raderna ritades tidigare av
 * ActivityCard med type="missed", alltså en röd/varnande ErrorRoundedIcon under
 * rubriken "Missed Activities". En vuxen som inte hann med frukosten behöver inte ett
 * utropstecken - hen behöver veta att det går att flytta den. Därför:
 *
 *   - neutral klocka i stället för varningsikon,
 *   - rubriken "Ouppmärksammade aktiviteter" i stället för "Missade",
 *   - och en synlig väg framåt: "Flytta" leder till samma edit-flöde som barnvyns
 *     MissedActionsBar använder (/edit-activity/:id/single/Edit).
 *
 * Raden i övrigt leder in i aktiviteten, precis som förut.
 */
const MissedActivities = () => {
  const { instances } = useContext(CalendarDayContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getRoutine } = useRoutines();
  const { selectedTheme, isDarkMode } = useThemePreference();
  const tk = getThemeTokens(selectedTheme, isDarkMode);

  const missedInstances = useMemo(
    () =>
      instances.filter(
        (instance) => isBefore(instance.startDate, new Date()) && !isDate(instance.dateIsStarted),
      ),
    [instances],
  );

  if (missedInstances.length === 0) return null;

  const handleMove = (activityInstanceId: number) => () => {
    // Samma städning som MissedActionsBar: ett kvarliggande utkast i sessionStorage
    // hade annars öppnat formuläret med FEL aktivitet.
    sessionStorage.removeItem('formActivity');
    navigate(`/edit-activity/${activityInstanceId}/single/Edit`);
  };

  return (
    <Stack spacing={1.25}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {t('daily.missed.title')}
      </Typography>

      {missedInstances.map((instance) => (
        <Box
          key={instance.activityInstanceId}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.75,
            backgroundColor: tk.card,
            border: `1px solid ${tk.border}`,
            borderRadius: '14px',
            p: '14px 16px',
          }}
        >
          <Box
            aria-hidden
            sx={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              bgcolor: `${tk.accent}26`,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <Clock size={17} weight="duotone" color={tk.accent} />
          </Box>

          <Box
            component="button"
            type="button"
            onClick={() => navigate(`/activity/${instance.activityInstanceId}`)}
            sx={{
              flex: 1,
              minWidth: 0,
              textAlign: 'left',
              background: 'none',
              border: 'none',
              p: 0,
              cursor: 'pointer',
              fontFamily: 'inherit',
              '&:focus-visible': { outline: '2px solid currentColor', outlineOffset: '2px' },
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: tk.text }} noWrap>
              {getRoutineStepLabel(instance, getRoutine(instance))}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: tk.textSoft }}>
              {format(new Date(instance.startDate), 'HH:mm')}–
              {format(addMinutes(new Date(instance.startDate), instance.duration), 'HH:mm')}
            </Typography>
          </Box>

          <Button
            onClick={handleMove(instance.activityInstanceId)}
            size="small"
            sx={{
              flexShrink: 0,
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '10px',
              whiteSpace: 'nowrap',
            }}
          >
            {t('daily.missed.move')} →
          </Button>
        </Box>
      ))}
    </Stack>
  );
};

export default MissedActivities;
